import { useEffect, useRef, useState, type MouseEvent, type PointerEvent } from 'react';

export type AbsenceDateRange = { startDate: string; endDate: string };

function rangeBetween(anchor: string, end: string): AbsenceDateRange {
  return anchor <= end ? { startDate: anchor, endDate: end } : { startDate: end, endDate: anchor };
}

// 날짜만 선택한다. 근무일과 실제 차감량은 기존 신청 계산 API가 판정한다.
export function useCalendarRangeSelection(onComplete?: (range: AbsenceDateRange) => void) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [range, setRange] = useState<AbsenceDateRange>();
  const gesture = useRef<{ pointerId: number; start: string; current: string; dragged: boolean } | undefined>(undefined);
  const suppressClick = useRef(false);
  const complete = useRef(onComplete);
  complete.current = onComplete;

  const cancel = () => {
    gesture.current = undefined;
    setRange(undefined);
  };

  useEffect(() => {
    if (!onComplete) cancel();
  }, [Boolean(onComplete)]);

  useEffect(() => {
    const dateAt = (event: globalThis.PointerEvent) => {
      const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>('[data-absence-date]');
      return target && containerRef.current?.contains(target) ? target.dataset.absenceDate : undefined;
    };
    const move = (event: globalThis.PointerEvent) => {
      const active = gesture.current;
      if (!active || active.pointerId !== event.pointerId) return;
      if ((event.buttons & 1) === 0) { cancel(); return; }
      const date = dateAt(event);
      if (!date || date === active.current) return;
      active.current = date;
      active.dragged ||= date !== active.start;
      if (active.dragged) setRange(rangeBetween(active.start, date));
    };
    const finish = (event: globalThis.PointerEvent) => {
      const active = gesture.current;
      if (!active || active.pointerId !== event.pointerId) return;
      gesture.current = undefined;
      if (!active.dragged) return;
      suppressClick.current = true;
      const date = dateAt(event);
      cancel();
      if (date) complete.current?.(rangeBetween(active.start, date));
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        suppressClick.current = Boolean(gesture.current);
        cancel();
      }
    };
    const interrupt = () => { suppressClick.current = Boolean(gesture.current); cancel(); };
    const cancelPointer = (event: globalThis.PointerEvent) => {
      if (gesture.current?.pointerId === event.pointerId) interrupt();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', cancelPointer);
    window.addEventListener('blur', interrupt);
    window.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', cancelPointer);
      window.removeEventListener('blur', interrupt);
      window.removeEventListener('keydown', escape);
    };
  }, []);

  return {
    containerRef,
    range,
    cancel,
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
      suppressClick.current = false;
      // 터치는 스크롤을 유지한다. 날짜 상세에서 신청창을 열어 기간을 지정할 수 있다.
      if (!complete.current || event.pointerType === 'touch' || event.button !== 0 || !event.isPrimary) return;
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-absence-date]');
      const date = target?.dataset.absenceDate;
      if (date) gesture.current = { pointerId: event.pointerId, start: date, current: date, dragged: false };
    },
    onClickCapture: (event: MouseEvent<HTMLDivElement>) => {
      if (!suppressClick.current || event.detail === 0) return;
      suppressClick.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
  };
}
